import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import TimelineIcon from '@mui/icons-material/Timeline';
import TriggerModal from './TriggerForm.tsx';
import Chart from 'react-apexcharts';
import { MaterialReactTable } from 'material-react-table';
import { useRecoilState } from 'recoil';
import { reportTemplateDetailsAtom } from '../../../../Services/_states/Report/index.ts';

type ChartType = 'bar' | 'stacked' | 'line' | 'area' | 'pie' | 'donut' | 'table';

const iconMap: Record<ChartType, JSX.Element> = {
  bar: <BarChartIcon sx={{ fontSize: 18, mr: 0.5 }} />,
  stacked: <StackedBarChartIcon sx={{ fontSize: 18, mr: 0.5 }} />,
  line: <ShowChartIcon sx={{ fontSize: 18, mr: 0.5 }} />,
  area: <TimelineIcon sx={{ fontSize: 18, mr: 0.5 }} />,
  pie: <PieChartIcon sx={{ fontSize: 18, mr: 0.5 }} />,
  donut: <DonutLargeIcon sx={{ fontSize: 18, mr: 0.5 }} />,
  table: <TableChartIcon sx={{ fontSize: 18, mr: 0.5 }} />,
};

const fallbackColorByType: Record<ChartType, string> = {
  bar: '#1976d2',
  stacked: '#5e35b1',
  line: '#2e7d32',
  area: '#00897b',
  pie: '#c62828',
  donut: '#ad1457',
  table: '#6d4c41',
};

// Helper function to get default dataset
const getDefaultDataset = () => [
  { category: 'A', value: 30, value2: 12 },
  { category: 'B', value: 50, value2: 20 },
  { category: 'C', value: 40, value2: 28 },
];

export default function ChartNode() {
  const [templateReport, setTemplateReport] = useRecoilState < any > (reportTemplateDetailsAtom);
  const [showModal, setShowModal] = useState < string | null > (null);

  const removeChart = (chartId: string) => {
    setTemplateReport((prev) => ({
      ...prev,
      charts: prev.charts.filter((c: any) => c.id !== chartId),
    }));
  };

  const updateChart = (chartId: string, updates: any) => {
    setTemplateReport((prev) => ({
      ...prev,
      charts: prev.charts.map((c: any) =>
        c.id === chartId ? { ...c, ...updates } : c
      ),
    }));
  };

  // ✅ Move useMemo to component level - process all charts' datasets at once
  const processedCharts = useMemo(() => {
    return templateReport?.charts?.map((chart: any) => ({
      ...chart,
      dataset: Array.isArray(chart.dataset) && chart.dataset.length
        ? chart.dataset
        : getDefaultDataset()
    })) || [];
  }, [templateReport?.charts]);

  // Plain renderer (no hooks here!)
  const renderBody = (chart: any, dataset: any[]) => {
    const { type, xField, yField, yFields } = chart;

    if (type === 'table') {
      const firstRow = dataset[0] || {};
      const columns = Object.keys(firstRow).map((key) => ({
        header: key,
        accessorKey: key,
      }));
      return (
        <MaterialReactTable
          columns={columns}
          data={dataset}
          enableColumnActions={false}
          enableColumnFilters={false}
          enablePagination={false}
          enableSorting={false}
          muiTableBodyCellProps={{ sx: { p: 1 } }}
          muiTableHeadCellProps={{ sx: { p: 1, fontWeight: 'bold' } }}
        />
      );
    }

    const safeX = xField || 'category';
    const primaryY = yField || (Array.isArray(yFields) && yFields[0]) || 'value';
    const safeYFields =
      Array.isArray(yFields) && yFields.length ? yFields : [primaryY];

    const isPieLike = type === 'pie' || type === 'donut';
    const categories = dataset.map((row) => String(row?.[safeX] ?? ''));

    let series: any[] = [];
    if (isPieLike) {
      series = dataset.map((row) => Number(row?.[primaryY] ?? 0));
    } else {
      series = safeYFields.map((field) => ({
        name: field,
        data: dataset.map((row) => Number(row?.[field] ?? 0)),
      }));
    }

    const baseOptions: any = {
      chart: { type: type === 'stacked' ? 'bar' : type },
      legend: { position: 'bottom' },
      grid: { padding: { right: 8, left: 8 } },
    };

    const options: any = isPieLike
      ? { ...baseOptions, labels: categories }
      : {
        ...baseOptions,
        xaxis: { categories },
        plotOptions: type === 'stacked' ? { bar: { stacked: true } } : {},
      };

    return (
      <Chart
        options={options}
        series={series}
        type={type === 'stacked' ? 'bar' : type}
        height={240}
      />
    );
  };

  return (
    <>
      {processedCharts.map((chart: any, index: number) => {
        const headerColor = chart.color || fallbackColorByType[chart.type];

        return (
          <Box
            key={chart.id ?? index}
            sx={{
              width: 300,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid #ccc',
              bgcolor: '#fff',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                bgcolor: headerColor,
                color: '#fff',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}
              >
                {iconMap[chart.type]}{' '}
                {chart.title || `${chart.serial}. ${chart.type.toUpperCase()} Chart`}
              </Typography>
              <Box>
                <IconButton
                  size="small"
                  sx={{ color: '#fff' }}
                  onClick={() => setShowModal(chart.id)}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{ color: '#fff' }}
                  onClick={() => removeChart(chart.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ p: 1 }}>{renderBody(chart, chart.dataset)}</Box>
          </Box>
        );
      })}

      {showModal && (
        <TriggerModal
          show={!!showModal}
          handleClose={() => setShowModal(null)}
          node={templateReport.charts.find((c: any) => c.id === showModal)}
          // onChange={(updates) => updateChart(showModal, updates)}
        />
      )}
    </>
  );
}